import React, {useEffect, useMemo, useState} from "react";
import {Button, Form, Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import ModalControls from "../../common/ModalControls";
import {useLocation, useNavigate} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {
  compareData,
  onCloseDetails,
  onItemsListDeleteButtonClick,
  onSaveAndClose,
  restoreFormData
} from "../../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import "../../../../css/DictionaryForms.css";

const currentYear = new Date().getFullYear();

const defaultItem = {
  id: "",
  name: "",
  shortName: "",
  date: "",
  description: ""
};

const NonWorkingDays = () => {
  const dataUrl = "/non-working-days";
  const itemName = "dzień wolny od pracy";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);
  const [selectedYear, setSelectedYear] = useState(String(currentYear));

  const yearOptions = useMemo(() => {
    const years = [];
    for (let year = currentYear - 2; year <= currentYear + 4; year += 1) years.push(year);
    return years;
  }, []);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false, visible: false},
    {label: "Data", accessor: "date", sortable: true, searchable: true},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrót", accessor: "shortName", sortable: true, searchable: true},
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const state = {
    itemsList,
    setItems,
    currentItem,
    setCurrentItem,
    currentFormState,
    setCurrentFormState,
    defaultItem,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const loadData = async () => {
    setLoading(true);
    const url = selectedYear ? `${dataUrl}?year=${selectedYear}` : dataUrl;
    const response = await getItems(url);
    if (response.status === 200) {
      setItems(response.data || []);
    } else if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    } else {
      alert("Nie udało się pobrać dni wolnych od pracy.");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [selectedYear, location.key]);

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, `${currentItem.name || ""} ${currentItem.date || ""}`.trim());
    }
  }, [allowDelete]);

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate("/login", {state: {from: location}, replace: true});
      return;
    }
    if (response) await loadData();
  };

  const onSaveItemClick = async event => {
    event.preventDefault();
    setUpdateConflict(false);

    if (!currentItem.name?.trim() || !currentItem.date) {
      if (!currentItem.name?.trim()) document.getElementById("name")?.classList.add("form-input-invalid");
      if (!currentItem.date) document.getElementById("date")?.classList.add("form-input-invalid");
      return;
    }

    const item = {
      id: currentItem.id || null,
      name: currentItem.name.trim(),
      shortName: currentItem.shortName?.trim() || null,
      date: currentItem.date,
      description: currentItem.description?.trim() || null
    };

    const response = currentFormState.formAddingDataMode
      ? await createItem(dataUrl, {...item, id: undefined}, state)
      : await updateItem(`${dataUrl}/${item.id}`, item, state);

    if (response?.status === 401 || response?.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    } else if (response?.status === 409) {
      setUpdateConflict(true);
    } else if (response && response.status >= 200 && response.status < 300) {
      onSaveAndClose({state});
      await loadData();
    }
  };

  const getCompleteItem = async id => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response.status === 200) return response.data;
    if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
      return null;
    }
    alert("Nie udało się pobrać dnia wolnego od pracy.");
    return null;
  };

  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowy dzień wolny od pracy",
    buttonTitle: "Dodaj dzień wolny"
  };

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>;
  } else if (itemsList.length > 0) {
    dataSectionContent = (
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        checkRelatedItems={() => setAllowDelete(true)}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={null}
        getCompleteItem={getCompleteItem}
      />
    );
  } else {
    dataSectionContent = (
      <div className="warn_info">
        <FontAwesomeIcon className="warning-icon div-visible" icon={faExclamationCircle}/>
        &nbsp;&nbsp;&nbsp;BRAK ZDEFINIOWANYCH DNI WOLNYCH DLA WYBRANEGO ROKU
      </div>
    );
  }

  const modalTitle = currentFormState.formAddingDataMode
    ? "Nowa pozycja: Dzień wolny od pracy"
    : `Edycja: ${currentItem.name || "Dzień wolny od pracy"}`;

  return (
    <div id="layoutSidenav_content" className="dictionary-page">
      <AppComponentCardHeader title="Pozycje słownika Dni wolne od pracy" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className="d-flex align-items-end gap-2 mb-3 flex-wrap">
          <Form.Group style={{minWidth: "190px"}}>
            <Form.Label className="mb-1">Rok</Form.Label>
            <Form.Select value={selectedYear} onChange={event => setSelectedYear(event.target.value)}>
              <option value="">Wszystkie lata</option>
              {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
            </Form.Select>
          </Form.Group>
          <div className="small text-muted pb-2">
            Zdefiniowane daty są traktowane jak dni wolne przy wyliczaniu norm czasu pracy i rozpoznawaniu dni roboczych w grafiku.
          </div>
        </div>

        <div className={itemsList.length > 0 ? "card shadow p-1 mb-5 bg-white rounded-2" : "card shadow mb-5 bg-transparent rounded-2"}>
          {dataSectionContent}
        </div>
      </div>

      <Modal
        show={currentFormState.showForm}
        onHide={onClose}
        backdrop="static"
        keyboard={false}
        className="dictionary-form-modal"
        dialogClassName="dictionary-form-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="dictionary-form-intro">
            <div>
              <strong>{currentFormState.formAddingDataMode ? "Dodajesz dzień wolny" : "Edytujesz dzień wolny"}</strong>
              <span>Data jest jednoznaczna. Ta sama nazwa może występować w kolejnych latach.</span>
            </div>
            {!currentFormState.formAddingDataMode && (
              <Button size="sm" variant="outline-secondary" onClick={() => restoreFormData({state})}>
                Przywróć wartości
              </Button>
            )}
          </div>

          <form className="dictionary-form-grid">
            <div className="dictionary-form-field">
              <label htmlFor="date"><span className="required">*</span> Data</label>
              <Form.Control
                id="date"
                type="date"
                value={currentItem.date || ""}
                onChange={event => {
                  setItemChanged(current => !current);
                  setCurrentItem(current => ({...current, date: event.target.value}));
                  setCurrentFormState(current => ({...current, formSaveButtonDisabled: false}));
                }}
              />
              <small>Data będzie wyłączona ze standardowych dni roboczych.</small>
            </div>

            <div className="dictionary-form-field">
              <label htmlFor="name"><span className="required">*</span> Nazwa</label>
              <TextInput propertyName="name" required={true} state={state}/>
              <small>Przykład: Święto Niepodległości.</small>
            </div>

            <div className="dictionary-form-field dictionary-form-field-short">
              <label htmlFor="shortName">Skrót</label>
              <TextInput propertyName="shortName" maxlen="20" required={false} state={state}/>
              <small>Opcjonalna krótka etykieta.</small>
            </div>

            <div className="dictionary-form-field dictionary-form-field-full">
              <label htmlFor="description">Opis</label>
              <TextArea propertyName="description" required={false} rows="3" state={state}/>
            </div>
          </form>

          {updateConflict && (
            <div className="dictionary-form-error">
              <FontAwesomeIcon icon={faInfoCircle}/>
              <span>Dla tej daty istnieje już zdefiniowany dzień wolny.</span>
            </div>
          )}
        </Modal.Body>

        <ModalControls
          onDelete={onDelete}
          onCloseDetails={onClose}
          onSubmit={onSaveItemClick}
          state={state}
        />
      </Modal>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
};

export default NonWorkingDays;
